// Subscribe to one conversation over the device socket (replay, then live) and keep its
// decrypted view up to date. The frames feed the pure reducer; each new segment kicks off
// one CEK unwrap with the device key, whose result feeds the reducer too.
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { unwrapCek } from "../device/crypto";
import type { LinkedDevice } from "../device/link";
import type { Proto } from "../proto/index";
import type { RelayClient } from "../relay/client";
import { ulidToBytes } from "../proto/ulid";
import { type ConversationState, emptyConversation } from "./model";
import { type ConversationAction, reduceConversation } from "./reducer";
import { type SendInput, type SendResult, sendInbound } from "./send";

export interface ConversationSource {
  proto: Proto;
  relay: RelayClient;
  device: LinkedDevice;
}

export interface ConversationHandle {
  state: ConversationState;
  send: (input: Omit<SendInput, "conversationId">) => Promise<SendResult>;
}

export function useConversation(source: ConversationSource, conversationId: string): ConversationHandle {
  const { proto, relay, device } = source;
  // React runs the reducer from the latest render, so a new context takes effect at once.
  const ctx = useMemo(() => ({ proto, conversationId: ulidToBytes(conversationId) }), [proto, conversationId]);
  const reducer = useCallback(
    (s: ConversationState, a: ConversationAction) => reduceConversation(ctx, s, a),
    [ctx],
  );
  const [state, dispatch] = useReducer(reducer, undefined, emptyConversation);

  useEffect(() => {
    const id = ulidToBytes(conversationId);
    const offFrame = relay.onFrame((frame) => dispatch({ type: "frame", frame }));
    const unsubscribe = relay.subscribe({ conversations: { ids: [id] } });
    relay.connect();
    return () => {
      offFrame();
      unsubscribe();
    };
  }, [relay, conversationId]);

  // One unwrap per pending segment; `pending` flips to unwrapped/missing when it lands.
  const started = useRef(new Set<string>());
  useEffect(() => {
    for (const segment of state.segments.values()) {
      if (segment.key.kind !== "pending" || started.current.has(segment.id)) continue;
      started.current.add(segment.id);
      unwrapCek(proto, device.keys, device.deviceIdBytes, segment.open)
        .then((cek) => dispatch({ type: "cek", segmentId: segment.id, cek }))
        .catch(() => dispatch({ type: "cek", segmentId: segment.id, cek: undefined }));
    }
  }, [state.segments, proto, device]);

  // `send` needs the committed state (segment keys), not the one it was created with.
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  }, [state]);
  const send = useCallback(
    async (input: Omit<SendInput, "conversationId">) => {
      const result = await sendInbound({ proto, relay, device }, latest.current, { ...input, conversationId });
      dispatch({ type: "sent", event: result.event });
      return result;
    },
    [proto, relay, device, conversationId],
  );

  return { state, send };
}
